import React from 'react';
import Viewport from "./Viewport";
import { Sprite } from '@pixi/react';
import { Filter, Texture } from 'pixi.js';
import { utils } from '../../utils';
import { useState, useEffect } from "react";
import * as PIXI from "pixi.js";
import PulsingSprite from "./PulsingSprite";
import BoardSprite from "./BoardSprite";
import { boardTileSize } from '../../env';
import { gameStateManager } from '../../App';
import PixiReturningMeepleAnimationHandler from "./PixiReturningMeepleAnimationHandler";
import PixiOnFeatureScoredHandler from "./PixiOnFeatureScoredHandler";
import PixiMeeplePlacementInteractionHandler from "./PixiMeeplePlacementInteractionHandler";
import PixiTilePlacementInteractionHandler from "./PixiTilePlacementInteractionHandler";
import PixiBoardTilesHandler from "./PixiBoardTilesHandler";
import PixiBoardMeeplesHandler from "./PixiBoardMeeplesHandler";
import { emit } from '../Socket/game.Emitters';


const textureCache = {}; // Global cache

const maxZoomScale = 1.8;

const END_GAME_PHASE_SCORED_FEATURE_CAMERA_MOVEMENT_DURATION = 700; // ms
const END_GAME_PHASE_SCORED_FEATURE_ANIM_DELAY = 800; // ms

const Background = ( { width, height, origin, alpha, renderScale } ) =>
{
  const [ texture, setTexture ] = useState( null );

  useEffect( () =>
  {
    const loadTexture = async () =>
    {
      try
      {
        const tex = await Texture.fromURL( "/background.jpg" );

        // Enable mipmapping for smoother zooming
        tex.baseTexture.mipmap = PIXI.MIPMAP_MODES.ON;
        tex.baseTexture.scaleMode = PIXI.SCALE_MODES.LINEAR;

        setTexture( tex );
      }
      catch( error )
      {
        console.error( "Failed to load background texture:", error );
      }
    };

    loadTexture();
  }, [] );

  if( !texture )
  {
    return null;
  }

  // Calculate tile dimensions and coverage
  const tileScale = 0.5 * renderScale;
  const tileWidth = texture.width * tileScale;
  const tileHeight = texture.height * tileScale;

  // Calculate how many tiles we need to cover the area plus padding for panning
  // Increase coverage area by 150% (2.5x total size)
  const coverageMultiplier = 2.5;
  const totalWidth = width * renderScale * coverageMultiplier;
  const totalHeight = height * renderScale * coverageMultiplier;
  const padding = Math.max( tileWidth, tileHeight ) * 4; // Increased padding for larger coverage

  const tilesX = Math.ceil( ( totalWidth + padding * 2 ) / tileWidth );
  const tilesY = Math.ceil( ( totalHeight + padding * 2 ) / tileHeight );

  // Calculate starting position to center the tiling and account for origin offset
  const startX = -( tilesX * tileWidth ) / 2 - ( origin.x % tileWidth ) - padding;
  const startY = -( tilesY * tileHeight ) / 2 - ( origin.y % tileHeight ) - padding;

  const backgroundTiles = [];

  for( let x = 0; x < tilesX; x++ )
  {
    for( let y = 0; y < tilesY; y++ )
    {
      backgroundTiles.push(
        <Sprite
          key={ `bg-tile-${x}-${y}` }
          texture={ texture }
          x={ startX + x * tileWidth }
          y={ startY + y * tileHeight }
          width={ tileWidth }
          height={ tileHeight }
          alpha={ alpha }
          anchor={ 0 }
        />
      );
    }
  }

  return <>{ backgroundTiles }</>;
};

const BoardTile = ( { ...props } ) =>
{
  return (
    <BoardSprite
      textureCache={ textureCache }
      { ...props }
    />
  );
};

const fieldShader = `

		precision mediump float;

		varying vec2 vTextureCoord;

		uniform sampler2D uSampler;

		uniform vec4 col1;
		uniform vec4 col2;
		uniform vec4 col3;
		uniform vec4 col4;
		uniform vec4 col5;
		uniform vec4 col6;
		uniform vec4 col7;
		uniform vec4 col8;

		void main(){

			vec4 fg = texture2D( uSampler, vTextureCoord );

	        float ratio = 80.0;

	        vec2 pixel = gl_FragCoord.xy;

	        float diagonal = (pixel.x + pixel.y) / 2.0;

	        float x = mod( diagonal, ratio);

	        float first4Ratio = ratio / 2.0;

	        float first4Check = step(x, first4Ratio);

	        float first4SubCheck = step(x, first4Ratio / 2.0);
	        float first4SubSubCheck1 = step(x, first4Ratio / 4.0);
	        float first4SubSubCheck2 = step(x, 3.0 * (first4Ratio / 4.0));

	        float second4SubCheck = step(x, first4Ratio + first4Ratio / 2.0);
	        float second4SubSubCheck1 = step(x, first4Ratio + first4Ratio / 4.0);
	        float second4SubSubCheck2 = step(x, first4Ratio + 3.0 * (first4Ratio / 4.0));


	        gl_FragColor = first4Check * (
	                          first4SubCheck * ( first4SubSubCheck1 * col1 + (1.0 - first4SubSubCheck1) * col2 ) +
	                          ( 1.0 - first4SubCheck ) * ( first4SubSubCheck2 * col3 + (1.0 - first4SubSubCheck2) * col4 )
	                        )
	                        + (1.0 - first4Check) * (
	                          second4SubCheck * ( second4SubSubCheck1 * col5 + (1.0 - second4SubSubCheck1) * col6 ) +
	                          ( 1.0 - second4SubCheck ) * ( second4SubSubCheck2 * col7 + (1.0 - second4SubSubCheck2) * col8 )
	                        );

	        gl_FragColor *= ( 1.0 - step( fg.a, 0.01 ) );
		}

`

class PixiBoard extends React.Component
{
  state = {
    tilePlacementState: {
      currentTilePlacementHash: null,
      currentPlacementRotationIndex: 0
    }
  };

  fieldDisplayFilterIndex = 0;
  fieldDisplayFilters = [];
  fieldDisplayFilterUniforms = [];

  hasCameraInitialised = false;
  isInitialized = false; // Flag to prevent double initialization in React Strict Mode

  lastPlayerTileHighlightIndex = 0;

  constructor( props )
  {
    super( props );

    this.viewportRef = React.createRef();
    this.meeplePlacementInteractionHandlerRef = React.createRef();
    this.tilePlacementInteractionHandlerRef = React.createRef();
  }

  componentDidMount()
  {
    // Prevent double initialization in React Strict Mode
    if( this.isInitialized ) return;
    this.isInitialized = true;

    // Setup default initial viewport FOV and zoom level

    const viewport = this.viewportRef.current;

    const { onDisableAutoCameraFramingMode } = this.props;


    if( viewport )
    {
      const { ticker } = viewport.app;
      ticker.add( this.onViewportUpdate );

      viewport.on( "drag-start", () =>
      {
        onDisableAutoCameraFramingMode();
        // console.log("Dragging started!");
      } );

      // viewport.on("drag-end", () => {
      //   console.log("Dragging ended!");
      // });

      viewport.on( "wheel", () =>
      {
        onDisableAutoCameraFramingMode();
        // console.log("Wheel zoom detected!");
      } );

      viewport.once( 'moved-end', () =>
      {
        this.hasCameraInitialised = true;
      } );

      // Initialize camera immediately on mount to prevent delay
      if( gameStateManager.autoCameraFramingModeState && !this.props.isEndGamePhaseCameraMode )
      {
        const { boardAreaBL, boardAreaTR } = this.props;
        this.calculateFocusedCameraSettings( boardAreaBL, boardAreaTR );
        this.positionCameraToSeeCurrentBounds( 0 ); // Set duration to 0 for immediate positioning
      }

      // // Optional: for any movement
      // viewport.on("moved", () => {
      //   console.log("Viewport moved (by drag, wheel, or animate)");
      // });
    }


    // Register for key down event listening
    window.addEventListener( 'keydown', this.handleKeyDown );

    gameStateManager.addOnAutoCameraFramingModeChangeCallback( this.onAutoCameraFramingModeChangeHandler );
    gameStateManager.addOnMeepleReturnAnimationRequestCallback( this.onMeepleReturnAnimationRequest );

    // Handle viewport interaction disabling on mount
    if( this.props.isEndGamePhaseCameraMode )
    {
      viewport.plugins.pause( 'drag' );
      viewport.plugins.pause( 'wheel' );
    }
    else
    {
      viewport.plugins.resume( 'drag' );
      viewport.plugins.resume( 'wheel' );
    }
  }

  componentDidUpdate( prevProps )
  {
    // Handle viewport interaction disabling on prop change
    const viewport = this.viewportRef.current;
    if( viewport && prevProps.isEndGamePhaseCameraMode !== this.props.isEndGamePhaseCameraMode )
    {
      if( this.props.isEndGamePhaseCameraMode )
      {
        viewport.plugins.pause( 'drag' );
        viewport.plugins.pause( 'wheel' );
      }
      else
      {
        viewport.plugins.resume( 'drag' );
        viewport.plugins.resume( 'wheel' );
      }
    }
  }

  componentWillUnmount()
  {
    // Clean up the keydown event listening
    window.removeEventListener( 'keydown', this.handleKeyDown );

    const viewport = this.viewportRef.current;

    if( viewport )
    {
      const { ticker } = viewport.app;
      if( ticker )
      {
        ticker.remove( this.onViewportUpdate );
      }

      viewport.off( "drag-start" );
      // viewport.off("drag-end");
      viewport.off( "wheel" );
      // viewport.off("moved");
    }

    gameStateManager.removeOnAutoCameraFramingModeChangeCallback( this.onAutoCameraFramingModeChangeHandler );
    gameStateManager.removeOnMeepleReturnAnimationRequestCallback( this.onMeepleReturnAnimationRequest );

    // Reset initialization flag
    this.isInitialized = false;
  }

  onAutoCameraFramingModeChangeHandler = ( state ) =>
  {
    if( state )
    {
      const {
        boardAreaBL,
        boardAreaTR
      } = this.props;

      this.calculateFocusedCameraSettings( boardAreaBL, boardAreaTR );

      this.positionCameraToSeeCurrentBounds();
    }
  }

  onMeepleReturnAnimationRequest = ( meepleData ) =>
  {
    if( this.props.isEndGamePhaseCameraMode )
    {
      // Work out the center coords for the feature
      let minCoord = utils.hashToCoord( meepleData.featureMinMaxBounds.min );
      let maxCoord = utils.hashToCoord( meepleData.featureMinMaxBounds.max );

      // Position camera at the new scored feature.
      this.calculateFocusedCameraSettings( minCoord, maxCoord );
      this.positionCameraToSeeCurrentBounds( END_GAME_PHASE_SCORED_FEATURE_CAMERA_MOVEMENT_DURATION );
    }
  }

  onViewportUpdate = () =>
  {
    if( this.props.isEndGamePhaseCameraMode )
    {
      return;
    }

    const viewport = this.viewportRef.current;
    if( viewport )
    {
      const {
        boardAreaBL,
        boardAreaTR,
        stageRenderData,
        sidePanelWidth
      } = this.props;

      let scale = viewport.scale.x;

      const renderScale = stageRenderData.scale;
      // const canvasWidth = stageRenderData.width;
      const playScreenWidth = stageRenderData.width - ( sidePanelWidth * renderScale );
      const playScreenHeight = stageRenderData.height;

      const cameraPanMaxOuterBoardCellWidth = 1.5;

      let tileOffset = boardTileSize * renderScale * viewport.scale.x;

      this.boardLimitBLWorldPosX = ( playScreenWidth / 2 ) - ( ( boardAreaBL.x - cameraPanMaxOuterBoardCellWidth ) * tileOffset );
      this.boardLimitBLWorldPosY = ( playScreenHeight / 2 ) + ( ( boardAreaBL.y - cameraPanMaxOuterBoardCellWidth ) * tileOffset );

      this.boardLimitTRWorldPosX = ( playScreenWidth / 2 ) - ( ( boardAreaTR.x + cameraPanMaxOuterBoardCellWidth ) * tileOffset );
      this.boardLimitTRWorldPosY = ( playScreenHeight / 2 ) + ( ( boardAreaTR.y + cameraPanMaxOuterBoardCellWidth ) * tileOffset );

      if( this.lastX === undefined || isNaN( this.lastX ) )
      {
        this.lastX = this.boardLimitBLWorldPosX;
      }
      if( this.lastY === undefined || isNaN( this.lastY ) )
      {
        this.lastY = this.boardLimitBLWorldPosY;
      }

      // console.log( 'Current Scale:', scale.toFixed( 2 ),
      //   "\npos x,y : ", viewport.x.toFixed( 2 ), viewport.y.toFixed( 2 ),
      //   "\ncenter x,y : ", viewport.center.x.toFixed( 2 ), viewport.center.y.toFixed( 2 ),
      //   "\nboardAreaBL: ", boardAreaBL.x, boardAreaBL.y,
      //   "\nboardAreaTR: ", boardAreaTR.x, boardAreaTR.y,
      //   "\nBL Limit", this.boardLimitBLWorldPosX.toFixed( 2 ), this.boardLimitBLWorldPosY.toFixed( 2 ),
      //   "\nTR Limit", this.boardLimitTRWorldPosX.toFixed( 2 ), this.boardLimitTRWorldPosY.toFixed( 2 ),
      //   '\nCanvas dims', canvasWidth, playScreenHeight,
      //   '\nPlayScreen dims', playScreenWidth, playScreenHeight,
      //   '\nrenderScale : ', renderScale,
      //   '\ntileOffset: ', tileOffset );

      let minZoomScale = this.targetZoomScale * 0.8;

      // Apply clamping if the scale exceeds min/max bounds
      if( scale < minZoomScale )
      {
        viewport.scale.set( minZoomScale ); // Apply the clamped scale

        // Prevent weird camera drifting while clamping
        viewport.x = this.lastX;
        viewport.y = this.lastY;
      }
      else if( scale > maxZoomScale )
      {
        viewport.scale.set( maxZoomScale ); // Apply the clamped scale

        // Prevent weird camera drifting while clamping
        viewport.x = this.lastX;
        viewport.y = this.lastY;
      }

      // Clamp camera to within 'cameraPanMaxOuterBoardCellWidth' tiles of the outer tile limits.
      if( viewport.x > this.boardLimitBLWorldPosX )
      {
        viewport.x = this.boardLimitBLWorldPosX;
      }
      if( viewport.x < this.boardLimitTRWorldPosX )
      {
        viewport.x = this.boardLimitTRWorldPosX;
      }
      if( viewport.y < this.boardLimitBLWorldPosY )
      {
        viewport.y = this.boardLimitBLWorldPosY;
      }
      if( viewport.y > this.boardLimitTRWorldPosY )
      {
        viewport.y = this.boardLimitTRWorldPosY;
      }

      this.lastX = viewport.x;
      this.lastY = viewport.y;
    }
  };

  convertIntegerColourToNormalisedRGB( integerColour, alpha )
  {
    // convert to hex based string
    let h = integerColour.toString( 16 );

    // convert to rgb
    let r = 0, g = 0, b = 0;

    if( h.length === 6 )
    {
      r = "0x" + h[ 0 ] + h[ 1 ];
      g = "0x" + h[ 2 ] + h[ 3 ];
      b = "0x" + h[ 4 ] + h[ 5 ];
    }

    return [ +r / 255.0, +g / 255.0, +b / 255.0, alpha ];
  }

  getFieldDisplayFilter( teamColours, alpha = 1 )
  {
    let fieldFilter = null;
    let fieldFilterUniforms = null;

    if( this.fieldDisplayFilterIndex < this.fieldDisplayFilters.length )
    {
      // Use a cached filter
      fieldFilter = this.fieldDisplayFilters[ this.fieldDisplayFilterIndex ];
      fieldFilterUniforms = this.fieldDisplayFilterUniforms[ this.fieldDisplayFilterIndex ];
    }
    else
    {
      // Create a new filter & uniforms
      fieldFilterUniforms = { col1: [ 1, 1, 1, 0 ], col2: [ 1, 1, 1, 0 ], col3: [ 1, 1, 1, 0 ], col4: [ 1, 1, 1, 0 ], col5: [ 1, 1, 1, 0 ], col6: [ 1, 1, 1, 0 ], col7: [ 1, 1, 1, 0 ], col8: [ 1, 1, 1, 0 ] };
      fieldFilter = new Filter( null, fieldShader, fieldFilterUniforms );

      this.fieldDisplayFilters.push( fieldFilter );
      this.fieldDisplayFilterUniforms.push( fieldFilterUniforms );
    }

    this.fieldDisplayFilterIndex++;

    if( !teamColours )
    {
      // Default to white if no colours given
      teamColours = [ 0xffffff ];
    }

    let stripesPerColour = Math.floor( 8.0 / teamColours.length );
    let teamColourIndex = 0;
    let lastColourSwitchIndex = 0;

    for( let idx = 0; idx < 8; idx++ )
    {
      if( idx - lastColourSwitchIndex === stripesPerColour )
      {
        teamColourIndex++;
        lastColourSwitchIndex = idx;
      }

      if( teamColourIndex < teamColours.length )
      {
        fieldFilterUniforms[ 'col' + ( idx + 1 ) ] = this.convertIntegerColourToNormalisedRGB( teamColours[ teamColourIndex ], alpha );
      }
      else
      {
        fieldFilterUniforms[ 'col' + ( idx + 1 ) ] = [ 0, 0, 0, 0 ];
      }
    }

    return fieldFilter;
  }

  positionCameraToBoardCoord = ( x, y, duration = 1000 ) =>
  {
    const viewport = this.viewportRef.current;

    if( viewport )
    {
      const {
        stageRenderData,
        sidePanelWidth
      } = this.props;

      const renderScale = stageRenderData.scale;
      const currentCameraZoomScale = viewport.scale.x;
      const centerCameraOffsetX = ( sidePanelWidth * renderScale * 0.5 ) / currentCameraZoomScale;

      // console.log( 'currentCameraZoomScale', currentCameraZoomScale, 'renderScale', renderScale, 'centerCameraOffsetX', centerCameraOffsetX );

      const worldPosX = x * boardTileSize * renderScale;
      const worldPosY = y * boardTileSize * renderScale;

      console.log( `positionCameraToBoardCoord ${x}, ${y} => ${worldPosX + centerCameraOffsetX}, ${worldPosY}` );

      // Position the 'camera' to show the middle of the board in the middle of the visible portion of the game canvas
      viewport.snap( worldPosX + centerCameraOffsetX, worldPosY, { removeOnComplete: true, time: duration } );
    }
  }

  calculateFocusedCameraSettings = ( boundsMin, boundsMax ) =>
  {
    const {
      stageRenderData,
      sidePanelWidth,
    } = this.props;

    const renderScale = stageRenderData.scale;
    const playScreenWidth = stageRenderData.width - ( sidePanelWidth * renderScale );
    const playScreenHeight = stageRenderData.height;

    const boardAreaWidth = boundsMax.x - boundsMin.x;
    const boardAreaHeight = boundsMax.y - boundsMin.y;
    const boardAreaCenter = { x: boundsMin.x + ( boardAreaWidth ) / 2, y: boundsMin.y + ( boardAreaHeight ) / 2 }

    // console.log( "boundsMin : ", boundsMin, "boundsMax", boundsMax );
    // console.log( "boardAreaCenter", boardAreaCenter );
    // console.log( "playScreenWidth : ", playScreenWidth, "playScreenHeight", playScreenHeight );

    // Fit to board area height
    const tileWidthPadding = 1.5;

    this.targetZoomScale = playScreenHeight / ( ( ( boardAreaHeight + 1 ) + ( tileWidthPadding * 2 ) ) * boardTileSize * renderScale );

    if( ( boardAreaWidth + 1 + ( tileWidthPadding * 2 ) ) * ( boardTileSize * renderScale ) * this.targetZoomScale > playScreenWidth )
    {
      // Uh-oh. Board needs to be fit to screen by width, not height.
      // console.log( 'Uh-oh. Board needs to be fit to screen by width, not height.' );

      this.targetZoomScale = playScreenWidth / ( ( ( boardAreaWidth + 1 ) + ( tileWidthPadding * 2 ) ) * boardTileSize * renderScale );
    }

    this.centerCameraOffsetX = ( sidePanelWidth * renderScale * 0.5 ) / this.targetZoomScale;
    this.targetBoardWorldPosX = ( boardAreaCenter.x * boardTileSize * renderScale ) + this.centerCameraOffsetX;
    this.targetBoardWorldPosY = boardAreaCenter.y * -1 * boardTileSize * renderScale;

    // console.log( "calculateFocusedCameraSettings() - targetZoomScale", this.targetZoomScale, "centerCameraOffsetX", this.centerCameraOffsetX, "targetBoardWorldPos", this.targetBoardWorldPosX, this.targetBoardWorldPosY );
  }


  positionCameraToSeeCurrentBounds = ( duration = 1000 ) =>
  {
    const viewport = this.viewportRef.current;

    if( viewport )
    {
      if( !this.hasCameraInitialised )
      {
        duration = 0;
      }

      // console.log( "viewport.animate - targetZoomScale: " + this.targetZoomScale + ", position: " + this.targetBoardWorldPosX + "," + this.targetBoardWorldPosY );

      viewport.animate( {
        scale: this.targetZoomScale,
        position: { x: this.targetBoardWorldPosX, y: this.targetBoardWorldPosY },
        time: duration,
        easing: 'easeInOutSine'
      } );
    }
  }


  getColoursWithIndexes( colours, indexes )
  {
    let colourList = [];

    for( let idx = 0; idx < indexes.length; idx++ )
    {
      colourList.push( colours[ indexes[ idx ] ] );
    }

    return colourList;
  }

  // Tile placement state management methods
  onTilePlacementClick = ( tileHash ) =>
  {
    this.setState( {
      tilePlacementState: {
        currentTilePlacementHash: tileHash,
        currentPlacementRotationIndex: 0
      }
    } );
  }

  onTilePlacementRotateClick = () =>
  {
    const { validTilePlacements } = this.props;
    const { tilePlacementState } = this.state;

    if( tilePlacementState.currentTilePlacementHash )
    {
      let numValidRotations = validTilePlacements[ tilePlacementState.currentTilePlacementHash ].length;
      this.setState( {
        tilePlacementState: {
          ...tilePlacementState,
          currentPlacementRotationIndex: ( tilePlacementState.currentPlacementRotationIndex + 1 ) % numValidRotations
        }
      } );
    }
  }

  onTilePlacementCancelClick = () =>
  {
    this.setState( {
      tilePlacementState: {
        currentTilePlacementHash: null,
        currentPlacementRotationIndex: 0
      }
    } );
  }

  onTilePlacementConfirmClick = () =>
  {
    const { tilePlacementState } = this.state;
    if( tilePlacementState.currentTilePlacementHash )
    {
      emit.playerTilePlacement( {
        tileHash: tilePlacementState.currentTilePlacementHash,
        rotationIndex: tilePlacementState.currentPlacementRotationIndex
      } );

      // Clear the tile placement state after confirming
      this.setState( {
        tilePlacementState: {
          currentTilePlacementHash: null,
          currentPlacementRotationIndex: 0
        }
      } );
    }
  }

  render()
  {
    const {
      screenWidth,
      screenHeight,
      stageRenderData,
      validTilePlacements,
      opponentValidTilePlacements,
      validMeeplePlacements,
      lastTilesPlacedByPlayers,
      lastPlayerTileHighlightRequests,
      boardTiles,
      boardMeeples,
      currentTileSprite,
      playerTeamColourID,
      playerTeamIndex,
      playerData,
      interactive,
      fieldStateData,
      teamColours,
      tileFolderName,
      consoleInfoHighlightTile,
      consoleInfoHighlightRequestIndex,
      numTilesRemaining,
      boardAreaBL,
      boardAreaTR,
      isEndGamePhaseCameraMode
    } = this.props;

    const renderScale = stageRenderData.scale;

    // console.log( "PixiBoard Render() Scale : ", renderScale );

    // Reset the field filter display index
    this.fieldDisplayFilterIndex = 0;

    let playerColourIDs = new Map();

    if( playerData )
    {
      for( let teamIndex in playerData.playersJoined )
      {
        playerColourIDs.set( teamIndex, playerData.playersJoined[ teamIndex ].colourID );
      }
    }

    let fieldStateSprites = [];

    if( fieldStateData )
    {
      for( let fieldIdx = 0; fieldIdx < fieldStateData.length; fieldIdx++ )
      {
        let fieldData = fieldStateData[ fieldIdx ];
        let fieldFilter = this.getFieldDisplayFilter( this.getColoursWithIndexes( teamColours, fieldData.winningTeamIndexes ), 0.5 );

        for( let tileIdx = 0; tileIdx < fieldData.tileData.length; tileIdx++ )
        {
          let fieldTileData = fieldData.tileData[ tileIdx ];

          let tileCoord = utils.hashToCoord( fieldTileData.tile );

          fieldStateSprites.push(

            <BoardTile
              key={ "fieldtile_" + fieldStateSprites.length }
              imageSrc={ `./tiles/${tileFolderName}/${fieldTileData.tileSpriteData.sprite}.png` }
              renderScale={ renderScale }
              x={ tileCoord.x }
              y={ -1 * tileCoord.y }
              angle={ fieldTileData.tileSpriteData.rotation * 90 }
              alpha={ 0.5 }
              filters={ [ fieldFilter ] }
            />

          );
        }
      }
    }




    // Compile the available turn tile placement sprites (split into base and controls)
    let tilePlacementBaseContent = <></>;
    let tilePlacementControlsContent = <></>;

    if( validTilePlacements )
    {
      const { tilePlacementState } = this.state;

      // Base content (placement options and preview)
      tilePlacementBaseContent = (
        <PixiTilePlacementInteractionHandler
          validTilePlacements={ validTilePlacements }
          currentTileSprite={ currentTileSprite }
          tileFolderName={ tileFolderName }
          stageRenderData={ stageRenderData }
          textureCache={ textureCache }
          renderMode="base"
          externalState={ tilePlacementState }
          onTilePlacementClick={ this.onTilePlacementClick }
          onTilePlacementRotateClick={ this.onTilePlacementRotateClick }
          onTilePlacementCancelClick={ this.onTilePlacementCancelClick }
          onTilePlacementConfirmClick={ this.onTilePlacementConfirmClick }
        />
      );

      // Controls content (interaction buttons)
      tilePlacementControlsContent = (
        <PixiTilePlacementInteractionHandler
          validTilePlacements={ validTilePlacements }
          currentTileSprite={ currentTileSprite }
          tileFolderName={ tileFolderName }
          stageRenderData={ stageRenderData }
          textureCache={ textureCache }
          renderMode="controls"
          externalState={ tilePlacementState }
          onTilePlacementClick={ this.onTilePlacementClick }
          onTilePlacementRotateClick={ this.onTilePlacementRotateClick }
          onTilePlacementCancelClick={ this.onTilePlacementCancelClick }
          onTilePlacementConfirmClick={ this.onTilePlacementConfirmClick }
        />
      );
    }
    else if( opponentValidTilePlacements )
    {
      // Show opponent tile placements (read-only, no controls)
      tilePlacementBaseContent = (
        <PixiTilePlacementInteractionHandler
          validTilePlacements={ opponentValidTilePlacements }
          currentTileSprite={ currentTileSprite }
          tileFolderName={ tileFolderName }
          stageRenderData={ stageRenderData }
          textureCache={ textureCache }
          renderMode="opponent"
          externalState={ null }
          onTilePlacementClick={ null }
          onTilePlacementRotateClick={ null }
          onTilePlacementCancelClick={ null }
          onTilePlacementConfirmClick={ null }
        />
      );

      // No controls for opponent placements
      tilePlacementControlsContent = <></>;
    }

    let pulsingTileHighlightsContent = [];

    if( lastTilesPlacedByPlayers )
    {
      for( let idx = 0; idx < lastTilesPlacedByPlayers.length; idx++ )
      {
        if( lastTilesPlacedByPlayers[ idx ] === null )
        {
          continue;
        }

        let placementCoords = utils.hashToCoord( lastTilesPlacedByPlayers[ idx ] );

        // Ignore showing a highlight for the players own tile placement. (They know where that one is!)
        if( playerTeamIndex === idx )
        {
          if( lastPlayerTileHighlightRequests[ idx ] === this.lastPlayerTileHighlightIndex )
          {
            continue;
          }
          else
          {
            this.lastPlayerTileHighlightIndex = lastPlayerTileHighlightRequests[ idx ];
          }
        }

        const teamColour = playerData.playersJoined[ idx ].colourHex;

        // console.log("lastTilesPlacedByPlayers[", idx, "] : " + lastTilesPlacedByPlayers[idx] + ", placementCoords : ", placementCoords, 'lastPlayerTileHighlightRequests[idx]', lastPlayerTileHighlightRequests[idx]);

        pulsingTileHighlightsContent.push(

          <PulsingSprite
            key={ "last-placed-tile-player-" + idx }
            imageSrc="./tiles/outer-tile-glow.png"
            renderScale={ renderScale }
            x={ placementCoords.x * boardTileSize }
            y={ -1 * placementCoords.y * boardTileSize }
            tint={ teamColour }
            startPulse={ lastPlayerTileHighlightRequests[ idx ] }	// NOTE: Incrementing this value will ensure a new pulsing sequence is started, even if the x/y is the same.
          />
        );
      }
    }


    if( consoleInfoHighlightTile != null )
    {
      pulsingTileHighlightsContent.push(

        <PulsingSprite
          key="consoleInfoHighlightTile"
          imageSrc="./tiles/outer-tile-glow.png"
          renderScale={ renderScale }
          x={ consoleInfoHighlightTile.x * boardTileSize }
          y={ -1 * consoleInfoHighlightTile.y * boardTileSize }
          startPulse={ consoleInfoHighlightRequestIndex }
          duration={ 1_000 }
          fadeOutDuration={ 1_000 }
        />
      );
    }

    let boardPlacedMeeplesContent = <></>;

    if( boardMeeples )
    {
      let meepleTileHashes = [];
      let meepleTileCoords = [];
      let meepleLocalCoords = [];
      let meepleIsFarmerStatus = [];
      let meepleTeamColourID = [];

      for( let teamIndex in boardMeeples )
      {
        let teamMeeples = boardMeeples[ teamIndex ];

        if( !teamMeeples )
        {
          continue;
        }

        const teamColourID = playerColourIDs.get( teamIndex.toString() );

        // console.log( "Team [" + teamIndex + "] teamColourID : {" + teamColourID + "} board meeples [" + teamMeeples.length + "] :" );
        // console.log( teamMeeples );

        for( let idx = 0; idx < teamMeeples.length; idx++ )
        {
          const teamMeeple = teamMeeples[ idx ];

          meepleTileHashes.push( teamMeeple.tileHash );
          meepleTileCoords.push( utils.hashToCoord( teamMeeple.tileHash ) );
          meepleLocalCoords.push( teamMeeple.localMeepleCoords );
          meepleIsFarmerStatus.push( teamMeeple.isFarmer );
          meepleTeamColourID.push( teamColourID );
        }
      }

      boardPlacedMeeplesContent =
        (
          <PixiBoardMeeplesHandler
            textureCache={ textureCache }
            meepleTileHashes={ meepleTileHashes }
            meepleTileCoords={ meepleTileCoords }
            meepleLocalCoords={ meepleLocalCoords }
            meepleIsFarmerStatus={ meepleIsFarmerStatus }
            meepleTeamColourID={ meepleTeamColourID } />
        );
    }





    // Compile the available meeple placement sprites
    let meeplePlacementContent = <></>;

    if( validMeeplePlacements )
    {
      meeplePlacementContent =
        (
          <PixiMeeplePlacementInteractionHandler
            ref={ this.meeplePlacementInteractionHandlerRef }
            validMeeplePlacements={ validMeeplePlacements }
            playerTeamColourID={ playerTeamColourID }
            numTilesRemaining={ numTilesRemaining }
            stageRenderData={ stageRenderData }
            textureCache={ textureCache }
          />
        );
    }


    if( gameStateManager.autoCameraFramingModeState && !isEndGamePhaseCameraMode )
    {
      this.calculateFocusedCameraSettings( boardAreaBL, boardAreaTR );

      // Only animate if camera has been initialized, otherwise position immediately
      const duration = this.hasCameraInitialised ? 1000 : 0;
      this.positionCameraToSeeCurrentBounds( duration );
    }

    return (

      <Viewport
        ref={ this.viewportRef }
        plugins={ [
          // Custom wheel zoom and drag implementations in Viewport.js
          {
            name: "decelerate",
            options: {
              friction: 0.85,     // Friction coefficient (0.1 = low friction/long deceleration, 0.99 = high friction/quick stop)
              bounce: 0.2,        // Bounce back when hitting viewport edges (0 = no bounce, 1 = full bounce)
              minSpeed: 0.1       // Minimum speed before stopping (higher = stops sooner)
            }
          }
        ] }
        screenWidth={ screenWidth }
        screenHeight={ screenHeight }
        eventMode={ interactive ? 'static' : 'none' }
        interactiveChildren={ interactive }
      >
        <Background origin={ { x: 0, y: 0 } } width={ 10000 } height={ 10000 } alpha={ 0.5 } renderScale={ renderScale } />

        <PixiBoardTilesHandler textureCache={ textureCache } tileFolderName={ tileFolderName } boardTiles={ boardTiles } />

        { fieldStateSprites }

        { pulsingTileHighlightsContent }

        { tilePlacementBaseContent }

        { boardPlacedMeeplesContent }

        { meeplePlacementContent }

        { tilePlacementControlsContent }

        <PixiReturningMeepleAnimationHandler textureCache={ textureCache } endGamePhaseAnimDelay={ END_GAME_PHASE_SCORED_FEATURE_ANIM_DELAY } />

        <PixiOnFeatureScoredHandler endGamePhaseAnimDelay={ END_GAME_PHASE_SCORED_FEATURE_ANIM_DELAY } />

      </Viewport>

    );
  }
}

export default PixiBoard;
